/* @flow */
import { type ContextRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { createAction, handleActions, type ActionType } from 'redux-actions';
import * as F from 'lodash/fp';
import * as L from 'partial.lenses';
import * as React from 'react';
import styled from 'styled-components';

import { Flex } from '@sonyged/proc-ui';
import css from '@styled-system/css';

import { type Customer, type Sushi, beltSlot } from '../../../../sushi/rules/types';
import { Overview } from '../../../../sushi/pages/Overview';
import { Random, Rng } from '../../../utils/Random';
import { Simulation } from '../../../../sushi/pages/Simulation';
import { SushiContext } from '../../../../sushi/SushiContext';
import { SushiCustomerStatModal } from './SushiCustomerStatModal';
import { SushiResultScreen } from '../../../../sushi/components/SushiResultScreen';
import { SushiSelectorScreen } from '../../../../sushi/components/SushiSelectorScreen';
import { WelcomeGuests } from '../../../../sushi/pages/WelcomeGuests';
import { customerGen } from '../../../../sushi/rules/thirdYear';
import { execute } from '../../../../sushi/rules/exec';
import { thirdYearMapper } from '../../../../sushi/mappers';
import AppContext from '../../../AppContext';
import CompletionPage from '../../common/CompletionPage';

type Props = {
  ...ContextRouter,
};

type RoundSetting = {
  firstHalf: Array<?Sushi>,
  secondHalf: Array<?Sushi>,
};

const emptyInput = [null, null, null, null, null, null, null, null];

function inputToBelt(input: Array<?Sushi>) {
  return input.map(sushi => (sushi ? beltSlot.filled(sushi) : beltSlot.empty));
}

const emptyRoundSetting: RoundSetting = {
  firstHalf: emptyInput,
  secondHalf: emptyInput,
};

type WelcomeGuestsScreen = { type: 'welcome' };
type OverviewScreen = { type: 'overview' };
type SettingFirstHalfScreen = { type: 'settingFirstHalf' };
type SimulationFirstHalfScreen = { type: 'simulationFirstHalf' };
type ResultFirstHalfScreen = { type: 'resultFirstHalf' };
type SettingSecondHalfScreen = { type: 'settingSecondHalf' };
type SimulationSecondHalfScreen = { type: 'simulationSecondHalf' };
type ResultSecondHalfScreen = { type: 'resultSecondHalf' };
type HistoryResultScreen = { type: 'historyResult', index: number };
type CompleteScreen = { type: 'complete' };

type View =
  | WelcomeGuestsScreen
  | OverviewScreen
  | SettingFirstHalfScreen
  | SimulationFirstHalfScreen
  | ResultFirstHalfScreen
  | SettingSecondHalfScreen
  | SimulationSecondHalfScreen
  | ResultSecondHalfScreen
  | HistoryResultScreen
  | CompleteScreen;

const view = {
  welcomeGuests: { type: 'welcome' },
  overview: { type: 'overview' },
  settingFirstHalf: { type: 'settingFirstHalf' },
  simulationFirstHalf: { type: 'simulationFirstHalf' },
  resultFirstHalf: { type: 'resultFirstHalf' },
  settingSecondHalf: { type: 'settingSecondHalf' },
  simulationSecondHalf: { type: 'simulationSecondHalf' },
  resultSecondHalf: { type: 'resultSecondHalf' },
  historyResult: (index: number) => ({ type: 'historyResult', index }),
  complete: { type: 'complete' },
};

type State = {|
  seed: number,
  currentRoundIndex: number,
  userInputs: { [string]: RoundSetting },
  currentView: View,
  customerStatModal: ?Customer,
|};

const init = (): State => ({
  seed: Math.random(),
  currentRoundIndex: 0,
  userInputs: {
    [0]: emptyRoundSetting,
    [1]: emptyRoundSetting,
    [2]: emptyRoundSetting,
  },
  currentView: view.overview,
  customerStatModal: null,
});

const inputLens = half => [
  L.choose(({ currentRoundIndex }) => {
    return ['userInputs', currentRoundIndex.toString(), half === 'first' ? 'firstHalf' : 'secondHalf'];
  }),
];

const next = createAction<'next', void>('next');
const back = createAction<'back', void>('back');
const selectSushi = createAction<'selectSushi', [Sushi, number]>('selectSushi');
const showCustomerStat = createAction<'showCustomerStat', Customer>('showCustomerStat');
const hideCustomerStat = createAction<'hideCustomerStat', void>('hideCustomerStat');
const showHistoryResult = createAction<'showHistoryResult', number>('showHistoryResult');

type Action =
  | ActionType<typeof next>
  | ActionType<typeof back>
  | ActionType<typeof selectSushi>
  | ActionType<typeof showCustomerStat>
  | ActionType<typeof hideCustomerStat>
  | ActionType<typeof showHistoryResult>;

let reducer = handleActions<State, _>(
  {
    [next.toString()]: state => {
      let { currentView } = state;
      switch (currentView.type) {
        case 'overview':
          if (state.currentRoundIndex >= 3) return { ...state, currentView: view.complete };
          else return { ...state, currentView: view.welcomeGuests };
        case 'welcome':
          return { ...state, currentView: view.settingFirstHalf };
        case 'settingFirstHalf':
          return { ...state, currentView: view.simulationFirstHalf };
        case 'simulationFirstHalf':
          return { ...state, currentView: view.resultFirstHalf };
        case 'resultFirstHalf':
          return { ...state, currentView: view.settingSecondHalf };
        case 'settingSecondHalf':
          return { ...state, currentView: view.simulationSecondHalf };
        case 'simulationSecondHalf':
          return { ...state, currentView: view.resultSecondHalf };
        case 'resultSecondHalf':
          return { ...state, currentView: view.overview, currentRoundIndex: state.currentRoundIndex + 1 };
        case 'historyResult':
          return state;
        case 'complete':
          return state;
        default:
          (currentView.type: empty);
          return state;
      }
    },
    [back.toString()]: state => {
      let { currentView } = state;
      switch (currentView.type) {
        case 'overview':
          return state;
        case 'welcome':
          return { ...state, currentView: view.overview };
        case 'settingFirstHalf':
          return { ...state, currentView: view.welcomeGuests };
        case 'simulationFirstHalf':
          return { ...state, currentView: view.settingFirstHalf };
        case 'resultFirstHalf':
          return { ...state, currentView: view.simulationFirstHalf };
        case 'settingSecondHalf':
          return { ...state, currentView: view.resultFirstHalf };
        case 'simulationSecondHalf':
          return { ...state, currentView: view.settingSecondHalf };
        case 'resultSecondHalf':
          return { ...state, currentView: view.simulationSecondHalf };
        case 'historyResult':
          return { ...state, currentView: view.overview };
        case 'complete':
          return state;
        default:
          (currentView.type: empty);
          return state;
      }
    },
    [showHistoryResult.toString()]: (state, action: ActionType<typeof showHistoryResult>) => ({
      ...state,
      currentView: view.historyResult(action.payload),
    }),
    [selectSushi.toString()]: (state, { payload: [sushi, index] }: ActionType<typeof selectSushi>) => {
      if (state.currentView.type === 'settingFirstHalf') return L.set([inputLens('first'), index], sushi, state);
      if (state.currentView.type === 'settingSecondHalf') return L.set([inputLens('second'), index], sushi, state);
      return state;
    },
    [showCustomerStat.toString()]: (state, { payload: customer }: ActionType<typeof showCustomerStat>) => ({
      ...state,
      customerStatModal: customer,
    }),
    [hideCustomerStat.toString()]: state => ({ ...state, customerStatModal: null }),
  },
  (null: any)
);

function SushiFieldChallengePage(props: Props) {
  const { history } = props;
  const [state, dispatch] = React.useReducer<State, Action, null>(reducer, null, init);
  const { seed, currentRoundIndex, currentView, customerStatModal } = state;

  let context = React.useContext(AppContext);
  React.useEffect(() => {
    if (!context) return;
    context.soundController.bgmPlay('proc_play04sushi', true);
  }, [context, currentView]);

  const mapper = thirdYearMapper;

  const customerGroups = React.useMemo(() => {
    const rng = new Rng(seed);
    return Random.traverse(
      [
        Random.arrayOfLength(customerGen, 1),
        Random.arrayOfLength(customerGen, 2).filter(
          customers => F.uniqBy(c => `${c.kind}-${c.ageGroup}-${c.gender}`, customers).length == 2
        ),
        Random.arrayOfLength(customerGen, 3).filter(
          customers => F.uniqBy(c => `${c.kind}-${c.ageGroup}-${c.gender}`, customers).length == 3
        ),
      ],
      x => x
    ).gen(rng);
  }, [seed]);

  const scores = [0, 1, 2].map(i => {
    let index = i;
    const customers = customerGroups[index];
    const prevStates = execute(
      customers,
      customers.map(() => []),
      inputToBelt(state.userInputs[index.toString()].firstHalf)
    );
    let customerStartState = prevStates[prevStates.length - 1].eatens;
    const states = execute(customers, customerStartState, inputToBelt(state.userInputs[index.toString()].secondHalf));
    const eatens = F.flatten(states[states.length - 1].eatens);
    return F.pipe(F.map(mapper.getSushiPrice), F.sum)(eatens);
  });

  const handleNext = React.useCallback(() => dispatch(next()), []);
  const handleBack = React.useCallback(() => dispatch(back()), []);
  const handleSushiChange = React.useCallback(payload => dispatch(selectSushi(payload)), []);
  const handleShowCustomerStat = React.useCallback(c => dispatch(showCustomerStat(c)), []);
  const handleHideCustomerStat = React.useCallback(() => dispatch(hideCustomerStat()), []);
  const gotoResult = React.useCallback(i => dispatch(showHistoryResult(i)), []);

  let view = null;
  switch (currentView.type) {
    case 'complete':
      view = <CompletionPage history={history} kind={'sushi'} totalScore={F.sum(scores)} />;
      break;
    case 'welcome': {
      const customers = customerGroups[currentRoundIndex].map(customer => {
        return { ...customer, orders: [] };
      });
      view = <WelcomeGuests customers={customers} onNext={handleNext} roundIndex={currentRoundIndex} />;
      break;
    }
    case 'overview':
      view = (
        <Overview
          done={currentRoundIndex >= 3}
          next={handleNext}
          viewResult={gotoResult}
          scores={scores}
          currentRoundIndex={currentRoundIndex}
          customerGroups={customerGroups}
        />
      );
      break;
    case 'settingFirstHalf': {
      const currentInput = L.get(inputLens('first'), state);
      const customerGroup = customerGroups[currentRoundIndex].map(customer => {
        return { ...customer, eatenCount: 0 };
      });
      view = (
        <SushiSelectorScreen
          onStart={handleNext}
          onBack={handleBack}
          onChange={handleSushiChange}
          showCustomerStat={handleShowCustomerStat}
          setting={currentInput}
          customerGroup={customerGroup}
          half="first"
          roundIndex={currentRoundIndex}
        />
      );
      break;
    }
    case 'settingSecondHalf': {
      const currentInput = L.get(inputLens('second'), state);
      const customers = customerGroups[currentRoundIndex];
      const states = execute(
        customers,
        customers.map(() => []),
        inputToBelt(L.get(inputLens('first'), state))
      );

      let eatenCounts = states[states.length - 1].eatens.map(x => x.length);
      const customerGroup = customerGroups[currentRoundIndex].map((customer, i) => {
        return { ...customer, eatenCount: eatenCounts[i] };
      });
      view = (
        <SushiSelectorScreen
          onStart={handleNext}
          onBack={handleBack}
          onChange={handleSushiChange}
          showCustomerStat={handleShowCustomerStat}
          setting={currentInput}
          customerGroup={customerGroup}
          half="second"
          roundIndex={currentRoundIndex}
        />
      );
      break;
    }
    case 'simulationFirstHalf': {
      const customers = customerGroups[currentRoundIndex];
      const states = execute(
        customers,
        customers.map(() => []),
        inputToBelt(L.get(inputLens('first'), state))
      );
      let customerStartState = null;
      view = (
        <Simulation
          customers={customers}
          half="first"
          onNext={handleNext}
          gameStates={states}
          customerStartState={customerStartState}
          roundIndex={currentRoundIndex}
        />
      );
      break;
    }
    case 'simulationSecondHalf': {
      const customers = customerGroups[currentRoundIndex];
      const prevStates = execute(
        customers,
        customers.map(() => []),
        inputToBelt(L.get(inputLens('first'), state))
      );
      let customerStartState = prevStates[prevStates.length - 1].eatens;
      const states = execute(customers, customerStartState, inputToBelt(L.get(inputLens('second'), state)));
      view = (
        <Simulation
          half="second"
          customers={customers}
          onNext={handleNext}
          gameStates={states}
          customerStartState={customerStartState}
          roundIndex={currentRoundIndex}
        />
      );
      break;
    }
    case 'resultFirstHalf': {
      let index: number = currentRoundIndex;

      const customers = customerGroups[index];
      const states = execute(
        customers,
        customers.map(() => []),
        inputToBelt(L.get(inputLens('first'), state))
      );
      const firstHalfStates = null;

      view = (
        <SushiResultScreen
          half="first"
          customers={customers}
          fromOverview={false}
          onNext={handleNext}
          lastState={states[states.length - 1]}
          firstHalfState={firstHalfStates && firstHalfStates[firstHalfStates.length - 1]}
          roundIndex={index}
        />
      );
      break;
    }
    case 'resultSecondHalf': {
      let index: number = currentRoundIndex;

      const customers = customerGroups[currentRoundIndex];
      const prevStates = execute(
        customers,
        customers.map(() => []),
        inputToBelt(L.get(inputLens('first'), state))
      );
      let customerStartState = prevStates[prevStates.length - 1].eatens;
      const states = execute(customers, customerStartState, inputToBelt(L.get(inputLens('second'), state)));

      view = (
        <SushiResultScreen
          half="second"
          customers={customers}
          fromOverview={false}
          onNext={handleNext}
          lastState={states[states.length - 1]}
          firstHalfState={prevStates && prevStates[prevStates.length - 1]}
          roundIndex={index}
        />
      );
      break;
    }
    case 'historyResult': {
      let index: number = currentView.index;

      const customers = customerGroups[index];
      const prevStates = execute(
        customers,
        customers.map(() => []),
        inputToBelt(state.userInputs[index.toString()].firstHalf)
      );
      let customerStartState = prevStates[prevStates.length - 1].eatens;
      const states = execute(customers, customerStartState, inputToBelt(state.userInputs[index.toString()].secondHalf));

      view = (
        <SushiResultScreen
          half="second"
          customers={customers}
          fromOverview={true}
          onNext={handleBack}
          lastState={states[states.length - 1]}
          firstHalfState={prevStates && prevStates[prevStates.length - 1]}
          roundIndex={index}
        />
      );
      break;
    }
    default:
      (currentView.type: empty);
  }

  return (
    <SushiContext.Provider value={mapper}>
      <PageContainer height="100%" flexDirection="column" className="sushi" id="proc-sushi-fields-root-page">
        {view}

        {customerStatModal && <SushiCustomerStatModal customer={customerStatModal} onClose={handleHideCustomerStat} />}
      </PageContainer>
    </SushiContext.Provider>
  );
}

const PageContainer = styled(Flex)(
  css({
    flexDirection: 'column',
    height: '100%',
  })
);

const SushiFieldChallengePageContainer = connect<Props, Props, _, _, _, _>(null, null)(SushiFieldChallengePage);

export default SushiFieldChallengePageContainer;
